import React, { useState, useMemo, useEffect } from "react";
import { IoIosArrowDropdown } from "react-icons/io";
import { RiResetLeftLine } from "react-icons/ri";
import { OverlayTrigger, Tooltip } from "react-bootstrap";
import { getCurrentFY, getFinancialYearMonthsPassed } from "../../../Utilitie";

const monthsRef = [
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec",
  "Jan",
  "Feb",
  "Mar",
];

const PDDController = ({
  data,
  year,
  setPDDFilteredSummaryData,
  openStates: propOpenStates,
  setOpenStates,
  setActiveMetrics,
}) => {
  // Fallback for openStates if not provided or incomplete
  const [internalOpenStates, setInternalOpenStates] = useState({
    "Financial Year": false,
    "Station Code": false,
    Month: false,
    Metrics: false,
  });
  // Use propOpenStates if provided, otherwise use internalOpenStates
  const openStates = propOpenStates || internalOpenStates;

  // State for search inputs (one per dropdown)
  const [searchValues, setSearchValues] = useState({
    "Financial Year": "",
    "Station Code": "",
    Month: "",
    Metrics: "",
  });

  // State for selections
  const [selectedFY, setSelectedFY] = useState(new Set());
  const [selectedStations, setSelectedStations] = useState(new Set());
  const [selectedMonths, setSelectedMonths] = useState(new Set());
  const [selectedMetrics, setSelectedMetrics] = useState(
    new Set(["total_trains_count"]), // Default to a single metric
  );
  useEffect(() => {
    setActiveMetrics(selectedMetrics);
  }, [selectedMetrics]);

  // Modified toggleSelection to handle single selection for Metrics
  const toggleSelection = (setFunc, currentSet, value, isMetrics = false) => {
    if (isMetrics) {
      // For Metrics, replace the current selection with the new value
      setFunc(new Set([value]));
    } else {
      // For other dropdowns, keep multi-selection behavior
      const newSet = new Set(currentSet);
      newSet.has(value) ? newSet.delete(value) : newSet.add(value);
      setFunc(newSet);
    }
  };

  const toggleDropdown = (title) => {
    const updateFunc = setOpenStates || setInternalOpenStates;
    updateFunc((prev) => ({
      ...prev,
      [title]: !prev[title],
    }));
  };

  const handleSearchChange = (title, value) => {
    setSearchValues((prev) => ({
      ...prev,
      [title]: value,
    }));
  };

  // Filtered data based on selections
  const filteredData = useMemo(() => {
    if (!data || !Array.isArray(data)) return [];

    return data
      .filter((entry) => selectedFY.has(entry.financial_year))
      .map((entry) => {
        const filteredStations = (entry.children || [])
          .filter((station) => selectedStations.has(station.station_code))
          .map((station) => {
            const filteredMonths = (station.children || [])
              .filter((month) => selectedMonths.has(month.month_name))
              // .map((month) => {
              //   const filteredData = (month.data || [])
              //     .map((d) => {
              //       const filteredEntry = {};
              //       selectedMetrics.forEach((metric) => {
              //         if (d.hasOwnProperty(metric)) {
              //           filteredEntry[metric] = d[metric];
              //         }
              //       });
              //       return filteredEntry;
              //     })
              //     .filter((d) => Object.keys(d).length > 0);
              //   return { ...month, data: filteredData };
              // })
              .filter((month) => month.data.length > 0);
            return { ...station, children: filteredMonths };
          })
          .filter((station) => station.children.length > 0);
        return { ...entry, children: filteredStations };
      })
      .filter((entry) => entry.children.length > 0);
  }, [data, selectedFY, selectedStations, selectedMonths, selectedMetrics]);

  // Available options for dropdowns
  const availableFY = useMemo(() => {
    return [...new Set((data || []).map((d) => d.financial_year))];
  }, [data]);

  const availableStations = useMemo(() => {
    const stations = [
      ...new Set(
        (data || [])
          .filter((entry) => selectedFY.has(entry.financial_year))
          .flatMap((entry) => entry.children || [])
          .map((station) => station.station_code),
      ),
    ];
    return stations;
  }, [data, selectedFY]);

  const availableMonths = useMemo(() => {
    const months = [
      ...new Set(
        (data || [])
          .filter((entry) => selectedFY.has(entry.financial_year))
          .flatMap((entry) => entry.children || [])
          .filter((station) => selectedStations.has(station.station_code))
          .flatMap((station) => station.children || [])
          .map((month) => month.month_name),
      ),
    ].sort((a, b) => {
      const indexA = monthsRef.findIndex((ref) =>
        a.toLowerCase().startsWith(ref.toLowerCase()),
      );
      const indexB = monthsRef.findIndex((ref) =>
        b.toLowerCase().startsWith(ref.toLowerCase()),
      );
      return indexA - indexB;
    });
    return months;
  }, [data, selectedFY, selectedStations]);

  const availableMetrics = useMemo(
    () => ["total_trains_count", "avg_avg_time", "total_total_time"],
    [],
  );

  // Default setup for initial state
  const defaultSetup = () => {
    if (!data?.length) return;

    const allFY = new Set(data.map((d) => d.financial_year));
    const allStations = new Set(
      data
        .flatMap((d) => d.children || [])
        .map((station) => station.station_code),
    );
    const allMonths = new Set(
      data
        .flatMap((d) => d.children || [])
        .flatMap((station) => station.children || [])
        .map((month) => month.month_name),
    );
    const defaultMetric = new Set(["total_trains_count"]); // Default to single metric

    const monthsPassed = getFinancialYearMonthsPassed(
      `${year}_${String(Number(year) + 1).slice(2, 4)}`,
      false,
    );

    setSelectedFY(allFY);
    setSelectedStations(allStations);
    setSelectedMonths(monthsPassed);
    setSelectedMetrics(defaultMetric);

    const updateFunc = setOpenStates || setInternalOpenStates;
    updateFunc({
      "Financial Year": false,
      "Station Code": false,
      Month: false,
      Metrics: false,
    });
  };

  useEffect(() => {
    defaultSetup();
  }, [data]);

  // Update filtered data
  useEffect(() => {
    setPDDFilteredSummaryData(filteredData);

    if (typeof setPDDFilteredSummaryData === "function") {
      setPDDFilteredSummaryData(filteredData);
    } else {
      console.warn(
        "setPDDFilteredSummaryData is not a function. Using internal state for filtered data.",
      );
    }
  }, [filteredData, setPDDFilteredSummaryData]);

  // Render checkbox or radio list for dropdowns
  const renderCheckboxList = (title, items, selectedSet, setFunc) => {
    const isMetrics = title === "Metrics";
    const filteredItems = items.filter((item) =>
      item
        ?.toString()
        .toLowerCase()
        .includes(searchValues[title].toLowerCase()),
    );

    return (
      <div className="col-md mt-2 p-1">
        <div
          className="bg-light"
          style={{
            position: "relative",
            fontSize: 12,
            borderRadius: "5px",
          }}
        >
          <div
            className="newUiBody"
            onClick={() => toggleDropdown(title)}
            style={{
              border: "1px solid #ccc",
              padding: "8px",
              cursor: "pointer",
              minWidth: "150px",
              maxHeight: "50px",
              overflow: "hidden",
              userSelect: "none",
            }}
          >
            Select {title}  
            <IoIosArrowDropdown
              style={{
                fontSize: 15,
                transition: "transform 0.3s ease",
                transform: openStates[title]
                  ? "rotate(180deg)"
                  : "rotate(0deg)",
              }}
            />
          </div>
          {openStates[title] && (
            <div
              className="newUiBody"
              style={{
                position: "absolute",
                top: "100%",
                left: 0,
                right: 0,
                border: "1px solid #ccc",
                background: "#fff",
                zIndex: 10,
              }}
            >
              <strong>{title}</strong>
              <input
                style={{ height: "25px", width: "90%" }}
                className="form-control ms-2 mb-1 me-2"
                type="search"
                placeholder={`Search ${title}...`}
                aria-label={`Search ${title}`}
                value={searchValues[title]}
                onChange={(e) => handleSearchChange(title, e.target.value)}
              />
              <hr className="m-0 p-0" />
              <div
                className="row suggestion mt-1"
                key={filteredItems.length}
                style={{
                  maxHeight: "250px",
                  minHeight: "50px",
                  overflowY: "auto",
                }}
              >
                {filteredItems.length > 0 ? (
                  filteredItems.map((item, index) => (
                    <div
                      key={index}
                      className="col-sm-4 fade-in"
                      style={{ fontSize: "12px" }}
                    >
                      <label
                        className="ms-1 text-start"
                        key={item}
                        style={{ display: "block", cursor: "pointer" }}
                      >
                        <input
                          className="form-check-input"
                          type={isMetrics ? "radio" : "checkbox"}
                          name={isMetrics ? "metrics-radio" : undefined}
                          checked={selectedSet.has(item)}
                          onChange={() =>
                            toggleSelection(
                              setFunc,
                              selectedSet,
                              item,
                              isMetrics,
                            )
                          }
                        />
                        {isMetrics
                          ? item
                              .replace("avg_avg_", "Avg ")
                              .replace("total_total_", "Total ")
                              .replace("total_trains_count", "Total trains")
                          : item}
                      </label>
                    </div>
                  ))
                ) : (
                  <div className="col-md-12 text-center">No items found</div>
                )}
              </div>
              <hr className="m-0 p-0" />
              {!isMetrics && (
                <div className="d-flex justify-content-evenly mt-1">
                  <button
                    className="btn btn-sm btn-outline-primary"
                    onClick={() => setFunc(new Set(items))}
                  >
                    Select All
                  </button>
                  <button
                    className="btn btn-sm btn-outline-success"
                    onClick={() => setFunc(new Set())}
                  >
                    Clear All
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    );
  };

  return (
    <div>
      <div
        className="row"
        style={{
          border: "1px solid #ccc",
          background: "rgba(255, 255, 255, 0.22)",
          boxShadow: "5px 5px 20px rgba(7, 7, 7, 0.3)",
          backdropFilter: "blur(15px)",
          WebkitBackdropFilter: "blur(10px)",
        }}
      >
        {renderCheckboxList(
          "Financial Year",
          availableFY,
          selectedFY,
          setSelectedFY,
        )}
        {renderCheckboxList(
          "Station Code",
          availableStations,
          selectedStations,
          setSelectedStations,
        )}
        {renderCheckboxList(
          "Month",
          availableMonths,
          selectedMonths,
          setSelectedMonths,
        )}
        {renderCheckboxList(
          "Metrics",
          availableMetrics,
          selectedMetrics,
          setSelectedMetrics,
        )}
        <OverlayTrigger
          delay={{ show: 100, hide: 200 }}
          overlay={<Tooltip id="tooltip-1">Reset Filter</Tooltip>}
        >
          <div
            className="btn btn-sm btn-danger d-flex justify-content-center align-items-center mt-1 mb-1"
            style={{
              maxWidth: "35px",
              boxShadow: "3px 1px 10px rgba(52, 80, 142, 0.5)",
            }}
            onClick={defaultSetup}
          >
            <RiResetLeftLine style={{ fontSize: "15px", fontWeight: "bold" }} />
          </div>
        </OverlayTrigger>
      </div>
    </div>
  );
};

export default PDDController;
